import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { cmsImageUrl } from "src/lib/cmsImageUrl";

/** Width covers the 443px desktop card at 2x; the source itself is only 1030 wide. */
const heroImage = cmsImageUrl(
  "77c9c2a0dc531694f81c29456a8ad4e37f55d286-1030x772.webp",
  { width: 900, quality: 75, autoFormat: true }
);

/* The white lives on Content, not on the panel or card, so no white surface sits
   behind the hero photo for a sub-pixel clip to expose as a hairline. The dialog's
   own safe-area padding moves onto Content for the same reason. */
export const modalStyle = css`
  --dialog-background: transparent;
  --border-radius: 0.5rem;
  --width: 21.4375rem;
  --max-width: 21.4375rem;

  section {
    padding-bottom: 0;
  }

  @media (min-width: 768px) {
    --width: 27.6875rem;
    --max-width: 27.6875rem;
  }
`;

/* Deliberately not clipping: the modal panel already clips to the rounded
   corners, and Hero's 1px bleed has to survive this box to sit under that
   clip. Clipping here would trim the bleed and reopen the hairline. */
export const Card = styled.div`
  position: relative;
  width: 100%;
`;

/* Bleeds 1px past the card on each side: the card clips it, so a sub-pixel
   rounding disagreement on a scaled compositing layer can't expose a white
   hairline beside the photo. */
export const Hero = styled.div`
  position: relative;
  height: 11rem;
  margin: 0 -1px;
  overflow: hidden;
  background-image: url(${heroImage});
  background-size: cover;
  background-position: center 42%;
  background-repeat: no-repeat;

  /* The white arc is the top of a circle far wider than the card — a shallow
     curve at this width. Matches the design's 4.26:1 circle-to-card ratio. */
  &::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 81.25%;
    width: 426.24%;
    aspect-ratio: 1;
    transform: translateX(-50%);
    border-radius: 50%;
    background: #fff;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(33, 33, 33, 0.32);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: #fff;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  svg path {
    stroke: currentColor;
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--Accessible-Blue, #3973da);
    outline-offset: 2px;
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0 1.5rem calc(2rem + env(safe-area-inset-bottom, 0px));
  background: #fff;
  text-align: center;
`;

export const Headline = styled.h2`
  margin: 0 0 0.25rem;
  font-family: var(--font-family-serif);
  font-size: 1.75rem;
  font-weight: 300;
  line-height: 1.2143;
  letter-spacing: -0.02em;
  color: var(--color-charcoal, #212121);
`;

export const Description = styled.p`
  margin: 0;
  font-family: var(--font-family);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.375;
  letter-spacing: -0.02em;
  color: var(--color-charcoal-80, rgba(33, 33, 33, 0.8));
`;

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;

  button * {
    text-transform: none;
  }
`;

/* A class rather than `styled(Button)`: emotion's styled() swallows the `theme`
   prop for its own ThemeProvider, so the Button would never see `danger`.
   `&&&` outranks the theme's `[data-theme="danger"]` rule on the same element. */
export const confirmButtonStyle = css`
  &&& {
    --local-color-danger: var(--Accessible-Red, #d04734);
  }
`;
